import * as AlephBet from 'alephbet';
import { Commit } from 'vuex';

import { SnowplowExperimentContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { UserExperiment, UserExperiments } from '@/stores/user.store';

export interface ExperimentMap {
	[key: string]: ExperimentConfig;
}

export interface ExperimentConfig {
	alias: string;
	countries: string[];
	id: string;
	languages: string[];
	name: string;
	platforms: string[];
	sample: number[];
	timestamps: {};
	variants: ExperimentVariant[];
	version?: string;
	description?: string;
}

export type ExperimentData = {
	name: string;
	variants: ExperimentVariant[];
	goals: AlephBet.Goal[];
};

export interface ExperimentVariant {
	name: string;
	weight: number;
	is_control?: boolean;
}

const proportion = Math.random();

/**
 * Debug mode for Alephbet
 */
// (AlephBet as any).options.debug = true;

/**
 * Correctly setup experiment context for given experiment, variant
 *
 * @param {any} experiment
 * @param {string} variant
 *
 * @returns {SnowplowExperimentContext} context
 */
const getExperimentContext = (experiment: AlephBet.Experiment, variant: string) => {
	const { name } = experiment;
	const { description, version } = experiment.options;

	return new SnowplowExperimentContext(name, version, variant, description);
};

const DEFAULT_EXPERIMENT_CONFIG = (tracking: boolean) => ({
	tracking_adapter: {
		experiment_start: (experiment: AlephBet.Experiment, variant: string) => {
			const { name } = experiment;

			// Add Experiment Context
			const context = getExperimentContext(experiment, variant);
			const properties = {
				action: variant,
				label: '__ACTIVATED__',
				nonInteraction: true,
			};
			if (tracking) {
				TrackingHelper.trackEvent(`EXP_${name}`, properties, [context]);
				console.log('[EXPERIMENT ACTIVATED]', name, variant);
			} else {
				console.log(`[EXPERIMENT WITHOUT TRACKING][ACTIVATED] EXP_${name} `, variant, experiment);
				console.log(`[EXPERIMENT WITHOUT TRACKING][SNOWPLOW] EXP_${name}: `, properties, [context]);
			}
		},

		goal_complete: (experiment: AlephBet.Experiment, variant: string, eventName: string) => {
			const { name } = experiment;

			// Add Experiment Context
			const context = getExperimentContext(experiment, variant);
			const properties = {
				action: variant,
				label: `GOAL_${eventName}`,
				nonInteraction: true,
			};

			if (tracking) {
				TrackingHelper.trackEvent(`EXP_${name}`, properties, [context]);
				console.log('[GOAL COMPLETED]', name, eventName, variant);
			} else {
				console.log(`[EXPERIMENT WITHOUT TRACKING][GOAL COMPLETED] EXP_${name} `, name, eventName, variant);
				console.log(`[EXPERIMENT WITHOUT TRACKING][SNOWPLOW] EXP_${name}:`, properties, [context]);
			}
		},
	},
});

const platform = 'web';

export const ExperimentHelper = {
	completeGoal(goal: AlephBet.Goal) {
		goal.complete();
	},
	addExperiment,
	isActive,
	getPlatform: () => platform,
};

function isActive(config?: ExperimentMap[keyof ExperimentMap], userExperiment?: UserExperiment) {
	return config && config.sample && config.platforms.indexOf(platform) !== -1 && userExperiment;
}

/**
 * adds an experiment and its goal.
 * it also checks if the experiment has been enabled for the running platform.
 */
function addExperiment(
	experimentData: ExperimentData,
	experimentsList: ExperimentMap,
	userExperiments: UserExperiments,
	userId: string | undefined,
	store: AlephBet.Store,
	commit: Commit,
	shouldBeActive = true
) {
	const { name } = experimentData;

	// don't add the experiment if it's not in the CMS experiment config
	if (!(name in experimentsList)) {
		return;
	}

	const config = experimentsList[name];
	const userExperiment = userExperiments[name];

	const active = isActive(config, userExperiment);
	if ((shouldBeActive && !active) || (!shouldBeActive && active)) {
		return;
	}

	const tracking = userExperiment?.tracking ?? true;
	const variants = userExperiment?.preferred
		? experimentData.variants.filter(variant => variant.name === userExperiment.variant)
		: experimentData.variants;

	const { version = '1', description = '' } = config;

	const experimentConfigObj = {
		name,
		user_id: userId,
		sample: checkSample(name, experimentsList),
		version: version.toString(),
		description,
		variants: variants.reduce((root: Record<string, { activate: () => void; weight: number }>, item) => {
			root[item.name] = {
				activate: () => {},
				weight: item.weight,
			};
			return root;
		}, {}),
	};

	const experiment = new AlephBet.Experiment({
		...DEFAULT_EXPERIMENT_CONFIG(tracking),
		...experimentConfigObj,
		storage_adapter: {
			get: (key: string) => store[key],
			set: (key: string, value: any) => {
				commit('user/SET_ALEPHBET_WITH_KEY_VALUE', { key, value }, { root: true });
			},
		},
	});

	// attach goals
	experimentData.goals?.forEach((goal: AlephBet.Goal) => {
		goal.add_experiment(experiment);
	});
}

/**
 * checks according to the experimentPreference and proportion (will be rolled once per session)
 * if a user is in the sample or not.
 * also take into account all other experiments for that platform.
 */
function checkSample(experimentName: string, experimentsList: ExperimentMap): 1 | 0 {
	if (!(experimentName in experimentsList)) return 0;

	const sample = experimentsList[experimentName].sample;
	if (Array.isArray(sample) && sample.length !== 2) {
		console.error('[ERROR] Only array of size 2 is allowed');
	}
	const start = Array.isArray(sample) ? sample[0] : 0;
	const end = Array.isArray(sample) ? sample[1] : sample;

	return <1 | 0>Number(start <= proportion && end > proportion);
}
